/**
 * /ja/summary — 要約 reframe bridge (JA, single-locale).
 *
 * Targets 要約とは (18k) / 要約コツ (3.5k) / 要約 ai (2.4k). Reframe: 要約は結論、
 * 決定は文脈付きの結論。No EN sibling.
 */
import { MarketingShell } from './MarketingShell';

export function JaSummary() {
  return (
    <MarketingShell
      currentLocale="ja"
      hreflangPath="/summary"
      hreflangLocales={['ja']}
      documentTitle="要約とは？コツ・やり方・AI要約の使い分け · Draftlize"
      documentDescription="要約の意味とコツ、AI要約の活用法をわかりやすく解説。さらに、要約より大事な「決定の記録」という観点まで踏み込みます。"
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">ガイド · 要約</span>
          <h1 className="serif" style={{ fontSize: 'clamp(34px, 4.6vw, 56px)', lineHeight: 1.06, margin: '12px 0 24px' }}>
            要約とは——<br />
            <em>「短くする」の先にあるもの。</em>
          </h1>
          <p className="lede">
            要約とは、長い文章や会話から要点だけを取り出して短くまとめること。AI 要約ツールを使えば数秒で終わります。ただ、仕事の現場で本当に必要なのは「短い結論」ではなく、<em>文脈付きの決定</em>です。なぜそう決めたのか、何を捨てたのか——要約はそこを削ぎ落とします。このページは、要約の基本とコツに加えて、要約より一段深い「決定を残す」という考え方までを扱います。
          </p>
        </div>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">上手な要約のコツ。</h2>
        </div>
        <div style={{ maxWidth: 760 }}>
          <ul style={{ fontSize: 16, lineHeight: 2, color: 'var(--ink-1)', margin: 0, paddingLeft: 20 }}>
            <li><strong>目的を先に決める</strong>——誰が何のために読む要約か</li>
            <li><strong>結論から書く</strong>——詳細は後ろに回す</li>
            <li><strong>固有名詞と数字は残す</strong>——抽象化しすぎない</li>
            <li><strong>「なぜ」を1行入れる</strong>——結論だけだと後で使えない</li>
          </ul>
          <p style={{ fontSize: 16, lineHeight: 1.9, color: 'var(--ink-1)', margin: '20px 0 0' }}>
            会議や録音が元なら、まず <a href="/ja/transcription" style={{ color: 'var(--ink-0)', textDecoration: 'underline' }}>文字起こし</a> してから要約すると速い。AI 要約はこの工程をまとめて自動化します。
          </p>
        </div>
      </section>

      <section className="compare">
        <div className="head">
          <div>
            <span className="label">結論 vs 文脈付きの結論</span>
            <h2 className="serif">要約は結論。<em>決定は、理由を連れた結論。</em></h2>
          </div>
          <p>要約は「何が話されたか」を短くする。決定の記録は「何を、なぜ決めたか」を最新に保つ。仕事で効くのは後者です。</p>
        </div>
        <table className="compare-table">
          <thead>
            <tr>
              <th></th>
              <th>要約</th>
              <th className="ours">決定の記録（Draftlize）</th>
            </tr>
          </thead>
          <tbody>
            <tr><td>残るもの</td><td>結論・要点</td><td className="ours yes">決定＋理由＋担当者</td></tr>
            <tr><td>「なぜ」</td><td className="no">たいてい削られる</td><td className="ours yes">カードに保持される</td></tr>
            <tr><td>前の決定を覆したとき</td><td className="no">別々の要約</td><td className="ours yes">リンクされ stale 表示</td></tr>
            <tr><td>数週間後</td><td className="partial">古いか分からない</td><td className="ours yes">最新か要確認かが一目</td></tr>
          </tbody>
        </table>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          要約は「結論」を残す。だが仕事を動かすのは、理由を連れた「決定」だ。
        </blockquote>
        <cite>要約で終わらせず、決定を文脈ごと残す。それが Draftlize。</cite>
      </section>

      <section className="compare" style={{ paddingTop: 64 }}>
        <div className="head">
          <div>
            <span className="label">FAQ</span>
            <h2 className="serif">よくある質問。</h2>
          </div>
        </div>
        <div style={{ maxWidth: 820 }}>
          {[
            { q: '要約とは簡単に言うと？', a: '長い文章や会話から要点だけを取り出し、短くまとめることです。目的に合わせて「何を残すか」を選ぶのがコツです。' },
            { q: 'AI 要約は正確？', a: '近年の AI 要約は実用的です。ただし「なぜ」を削りがちなので、後で判断材料として使うには理由を補う必要があります。' },
            { q: '要約と議事録の違いは？', a: '要約は要点の圧縮、議事録は会議の記録です。どちらも「決定」を構造化して残すと、後から本当に役立ちます。' + '' },
            { q: '要約した内容をどう活かす？', a: '結論だけでなく、その裏の決定を Draftlize にカードとして残します。別の決定が覆したら自動で stale 表示になり、古い結論に気づけます。詳しくは ' },
          ].map((f, i) => (
            <div key={f.q} style={{ padding: '18px 0', borderTop: i === 0 ? 0 : '1px dashed var(--rule)' }}>
              <h3 className="serif" style={{ fontSize: 18, margin: '0 0 8px', color: 'var(--ink-0)' }}>{f.q}</h3>
              <p style={{ margin: 0, fontSize: 15, lineHeight: 1.9, color: 'var(--ink-1)' }}>
                {f.a}
                {i === 3 && (
                  <a href="/ja/meeting-minutes-ai" style={{ color: 'var(--ink-0)', textDecoration: 'underline' }}>議事録ガイド</a>
                )}
                {i === 3 && ' も参照。'}
              </p>
            </div>
          ))}
        </div>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">要約の先へ。決定を文脈ごと残す<em>——$5 無料で。</em></h2>
        </div>
        <div className="trust-strip">
          <span>新規登録で $5 付与</span>
          <span>使った分だけ支払い</span>
          <span>残高は無期限</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            要約ツールはそのまま。結論の裏にある決定を Draftlize に残せば、理由ごと最新に保たれ、AI エージェントも読める。次の判断が速くなります。
          </p>
          <a className="btn-primary" href="/">$5 無料で始める</a>
        </div>
      </section>
    </MarketingShell>
  );
}
